import axios from "axios";
import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { userIdAtom } from "@/atoms/authAtoms";
import { getApiBaseUrl } from "@/config/api";
import type {
  DevicePanelStudiesResponse,
  DevicePanelStudyResponse,
  DevicePanelSummary,
} from "@/types/devicePanel";
import {
  sameVerdictSlot,
  type ReviewProgressResponse,
  type ReviewRound,
  type ReviewTarget,
  type ReviewVerdict,
  type StudyReviewResponse,
  type VerdictValue,
} from "@/types/devicePanelReview";

/** Query keys, each led by the signed-in user: what one user was served is never
 * served to the next on the same tab, whatever clears the cache. */
export const devicePanelKeys = {
  list: (user: string | null) => ["device-panels", user] as const,
  studies: (user: string | null, panelId: string) => ["device-panel", user, panelId] as const,
  study: (user: string | null, panelId: string, studyUid: string) =>
    ["device-panel", user, panelId, studyUid] as const,
  reviewProgress: (user: string | null, panelId: string, round: ReviewRound) =>
    ["device-panel-review", user, panelId, { round }] as const,
  studyReview: (user: string | null, panelId: string, studyUid: string) =>
    ["device-panel-review", user, panelId, studyUid] as const,
};

const panelRoot = (panelId: string) =>
  `${getApiBaseUrl()}/api/device-panels/${encodeURIComponent(panelId)}`;
const panelUrl = (panelId: string) => `${panelRoot(panelId)}/studies`;
const studyUrl = (panelId: string, studyUid: string) =>
  `${panelUrl(panelId)}/${encodeURIComponent(studyUid)}`;

/** The panel's own route for a study's images (`CaseViewer` `imagesUrl`). */
export const panelImagesUrl = (panelId: string, studyUid: string) =>
  `${studyUrl(panelId, studyUid)}/images`;

/** The panels the signed-in user may open. */
export function useDevicePanels(token: string | null, enabled: boolean) {
  const user = useAtomValue(userIdAtom);
  return useQuery({
    queryKey: devicePanelKeys.list(user),
    queryFn: async () =>
      (
        await axios.get(`${getApiBaseUrl()}/api/device-panels`, {
          headers: { Authorization: `Bearer ${token}` },
        })
      ).data as DevicePanelSummary[],
    enabled: !!token && enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/** A device panel's studies, with each device's flagged-at-default count per study. */
export function useDevicePanelStudies(token: string | null, panelId: string | undefined) {
  const user = useAtomValue(userIdAtom);
  return useQuery({
    queryKey: devicePanelKeys.studies(user, panelId ?? ""),
    queryFn: async () =>
      (await axios.get(panelUrl(panelId!), { headers: { Authorization: `Bearer ${token}` } }))
        .data as DevicePanelStudiesResponse,
    enabled: !!token && !!panelId,
    staleTime: 5 * 60 * 1000,
  });
}

const studyQuery = (
  token: string | null,
  user: string | null,
  panelId: string,
  studyUid: string,
) => ({
  queryKey: devicePanelKeys.study(user, panelId, studyUid),
  queryFn: async () =>
    (
      await axios.get(studyUrl(panelId, studyUid), {
        headers: { Authorization: `Bearer ${token}` },
      })
    ).data as DevicePanelStudyResponse,
  staleTime: 5 * 60 * 1000,
});

/** One study's output from every device on the panel, in display order. */
export function useDevicePanelStudy(
  token: string | null,
  panelId: string | undefined,
  studyUid: string | undefined,
) {
  const user = useAtomValue(userIdAtom);
  return useQuery({
    ...studyQuery(token, user, panelId ?? "", studyUid ?? ""),
    enabled: !!token && !!panelId && !!studyUid,
  });
}

/** Loads a study ahead of opening it, so the case opens on cached output. */
export function usePrefetchDevicePanelStudy(token: string | null, panelId: string) {
  const user = useAtomValue(userIdAtom);
  const queryClient = useQueryClient();
  return useCallback(
    (studyUid: string) => {
      if (token) void queryClient.prefetchQuery(studyQuery(token, user, panelId, studyUid));
    },
    [queryClient, token, user, panelId],
  );
}

/** The study's review queue items and verdicts in both rounds (the caller's, and
 * others' for internal callers); read only while `reviewing`. */
export function useStudyReview(
  token: string | null,
  panelId: string | undefined,
  studyUid: string | undefined,
  reviewing: boolean,
) {
  const user = useAtomValue(userIdAtom);
  return useQuery({
    queryKey: devicePanelKeys.studyReview(user, panelId ?? "", studyUid ?? ""),
    queryFn: async () =>
      (
        await axios.get(`${studyUrl(panelId!, studyUid!)}/review`, {
          headers: { Authorization: `Bearer ${token}` },
        })
      ).data as StudyReviewResponse,
    enabled: reviewing && !!token && !!panelId && !!studyUid,
  });
}

/** One round's queue: each queued study's size and the caller's progress through it.
 * A caller who is not one of the panel's reviewers gets an empty queue. */
export function useReviewProgress(
  token: string | null,
  panelId: string | undefined,
  round: ReviewRound,
) {
  const user = useAtomValue(userIdAtom);
  return useQuery({
    queryKey: devicePanelKeys.reviewProgress(user, panelId ?? "", round),
    queryFn: async () =>
      (
        await axios.get(`${panelRoot(panelId!)}/review`, {
          params: { round },
          headers: { Authorization: `Bearer ${token}` },
        })
      ).data as ReviewProgressResponse,
    enabled: !!token && !!panelId,
  });
}

export interface VerdictInput extends ReviewTarget {
  round: ReviewRound;
  verdict: VerdictValue;
  note: string | null;
}

/** The backend's reason for a failed request, else the request's own message. */
export function verdictErrorText(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
    return typeof detail === "string" ? detail : error.message;
  }
  return error instanceof Error ? error.message : String(error);
}

/**
 * Save the caller's verdict on one finding in one round; the other round's
 * verdict is never touched. The study's review updates at once
 * (a drafter or raw write can take a second or two while the backend re-reads the
 * output); a failed write puts the previous verdict back and rethrows for the
 * caller to show. Progress refreshes after every write.
 */
export function useSaveVerdict(token: string | null, panelId: string, studyUid: string) {
  const queryClient = useQueryClient();
  const user = useAtomValue(userIdAtom);
  const key = devicePanelKeys.studyReview(user, panelId, studyUid);
  return useMutation({
    mutationFn: async (input: VerdictInput) =>
      (
        await axios.put(`${studyUrl(panelId, studyUid)}/review/verdict`, input, {
          headers: { Authorization: `Bearer ${token}` },
        })
      ).data as ReviewVerdict,
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<StudyReviewResponse>(key);
      if (previous)
        queryClient.setQueryData<StudyReviewResponse>(key, {
          ...previous,
          verdicts: [
            ...previous.verdicts.filter((verdict) => !sameVerdictSlot(verdict, input)),
            {
              ...input,
              reviewer: previous.reviewer,
              updated_at: new Date().toISOString(),
            },
          ],
        });
      return { previous };
    },
    onSuccess: (saved) =>
      queryClient.setQueryData<StudyReviewResponse>(key, (current) =>
        current
          ? {
              ...current,
              verdicts: [
                ...current.verdicts.filter((verdict) => !sameVerdictSlot(verdict, saved)),
                saved,
              ],
            }
          : current,
      ),
    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSettled: (_saved, _error, input) =>
      queryClient.invalidateQueries({
        queryKey: devicePanelKeys.reviewProgress(user, panelId, input.round),
      }),
  });
}
